import { Parcel } from 'app/core/app.model';
import { AsyncItem, AsyncItemType } from 'app/shared/async-item';


export interface TaskResult {
    key: string;
    failed: boolean;
}

interface Field {
    type?: AsyncItemType;
    editable?: boolean;
    deletable?: boolean;
}

type Groups = { [taskKey: string]: { [fieldKey: string]: Field } };

export const summaryFields: Groups = {
    SummarySheetLtsa: {
        registeredOwner: { editable: true, deletable: true },
        showCredentialsMsg: {},
    },
    SummarySheetWalkscore: {
        walkscore: { deletable: true },
        transitscore: { deletable: true },
    },
    SummarySheetElevation: {
        minElevation: { type: 'meters', deletable: true },
        maxElevation: { type: 'meters', deletable: true },
    },
    SummarySheetPlanning: {
        planningLotSize: { editable: true, deletable: true },
        planningPlanningLine: {},
        planningZoningLine: {},
        planningDevelopmentApplications: { editable: true },
    },
    SummarySheetParagonTax: {
        annualTaxes: { editable: true, deletable: true },
        taxYear: { editable: true, deletable: true },
        bathrooms: { editable: true, deletable: true },
        bedrooms: { editable: true, deletable: true },
        floorArea: { editable: true, deletable: true },
        planNumber: {},
        structure: {},
        yearBuilt: { editable: true, deletable: true },

        salesHistory: {},

        assessmentYear: {},
        assessmentYearPrev: {},

        currentYearBuildingValue: { type: 'currency0' },
        currentYearLandValue: { type: 'currency0' },
        currentYearTotalValue: { type: 'currency0' },

        previousYearBuildingValue: { type: 'currency0' },
        previousYearLandValue: { type: 'currency0' },
        previousYearTotalValue: { type: 'currency0' },

        buildingGrowth: { type: 'number2' },
        landGrowth: { type: 'number2' },
        totalGrowth: { type: 'number2' },
    },
    SummarySheetOfferRent: {
        offerRentDate: { type: 'date' },
        offerRentMax: { type: 'currency0' },
        offerRentMin: { type: 'currency0' },
    },
    SummarySheetMlsHistory: {
        mlsHistory: { editable: true },
    },
    SummarySheetTiedParcels: {
        pids: {},
        legalDescriptions: {},
    }
};

interface SalesHistoryRecord {
    date: string;
    amount: number;
}

export type SalesHistoryData = SalesHistoryRecord[];

export interface SalesHistoryItem {
    date: AsyncItem;
    amount: AsyncItem;
    growth: AsyncItem;
}


const toNumber = (value: string | number): number => {
    if (typeof value === 'undefined' || value === null) {
        return 0;
    }
    if (typeof value === 'number') {
        return value;
    }
    return parseFloat(value.replace(/[^0-9.]+/g, ''));
};


export const calculateGrowth = (newValue: string | number | null, oldValue: string | number | null): number | null => {
    if (newValue === null || oldValue === null) {
        return null;
    }
    const growth = (toNumber(newValue) / toNumber(oldValue) - 1) * 100;
    return isNaN(growth) || Math.abs(growth) === Infinity ? null : growth;
};


/**
 * Returns sales history table structure with computed growth
 * from items containing date and amount
 */
export const buildSalesHistory = (salesHistoryData: SalesHistoryData): SalesHistoryItem[] => {
    let lastAmount: number | null = null;
    let growth: number | null = null;
    const history: SalesHistoryItem[] = [];
    for (const sale of [...salesHistoryData].reverse()) {
        if (lastAmount != null) {
            growth = calculateGrowth(sale.amount, lastAmount);
        }
        lastAmount = sale.amount;
        const historyItem = {
            date: new AsyncItem(sale.date, 'done', 'date'),
            amount: new AsyncItem(sale.amount, 'done', 'currency0'),
            growth: new AsyncItem(growth, 'done', 'number'),
        };
        history.push(historyItem);
    }
    history.reverse();
    return history;
};


export class EditableBoolean {

    constructor(readonly value: boolean, private originalValue?: boolean) {}

    resolve(value: boolean) {
        return new EditableBoolean(value, value);
    }

    reject(_reason: string) {
        return new EditableBoolean(false, false);
    }

    edit(value: boolean) {
        return new EditableBoolean(value, typeof this.originalValue !== 'undefined' ? this.originalValue : this.value);
    }

    restore() {
        if (typeof this.originalValue === 'undefined') {
            return this;
        }
        return this.resolve(this.originalValue);
    }
}

export interface SummaryData {
    showCredentialsMsg: boolean;

    loaded: { [taskKey: string]: boolean };

    editing: boolean;

    title: string;
    googleStreetViewImage: string | null;

    walkscore: AsyncItem;
    transitscore: AsyncItem;
    minElevation: AsyncItem;
    maxElevation: AsyncItem;
    registeredOwner: AsyncItem;
    ownerType: AsyncItem;

    annualTaxes: AsyncItem;
    taxYear: AsyncItem;
    bathrooms: AsyncItem;
    bedrooms: AsyncItem;
    floorArea: AsyncItem;
    planNumber: AsyncItem;
    planningLotSize: AsyncItem;
    structure: AsyncItem;
    yearBuilt: AsyncItem;
    pids: AsyncItem;
    legalDescriptions: AsyncItem;

    assessmentYear: AsyncItem;
    assessmentYearPrev: AsyncItem;

    currentYearBuildingValue: AsyncItem;
    currentYearLandValue: AsyncItem;
    currentYearTotalValue: AsyncItem;

    previousYearBuildingValue: AsyncItem;
    previousYearLandValue: AsyncItem;
    previousYearTotalValue: AsyncItem;

    buildingGrowth: AsyncItem;
    landGrowth: AsyncItem;
    totalGrowth: AsyncItem;

    appreciationLoaded: boolean;
    appreciationCurrentlyListed: boolean;

    appreciationActualAmount: AsyncItem;
    appreciationActualDate: AsyncItem;
    appreciationActualGrowth: AsyncItem;
    salesHistory: SalesHistoryItem[];

    offerRentTaskRun: boolean;
    showOfferRent: EditableBoolean;
    offerRentDate: AsyncItem;
    offerRentMax: AsyncItem;
    offerRentMin: AsyncItem;

    showMlsHistory: EditableBoolean;
    showMlsHistoryFirm: EditableBoolean;
    mlsHistory: AsyncItem;

    planningPlanningLine: AsyncItem;
    planningZoningLine: AsyncItem;
    planningDevelopmentApplications: AsyncItem;

    attributions: string[];
}

export const summaryInitial: SummaryData = {
    showCredentialsMsg: false,

    loaded: {},

    editing: false,

    title: '',
    googleStreetViewImage: null,

    walkscore: new AsyncItem(null, 'loading', 'text', { deletable: true }),
    transitscore: new AsyncItem(null, 'loading', 'text', { deletable: true }),
    minElevation: new AsyncItem(null, 'loading', 'meters', { deletable: true }),
    maxElevation: new AsyncItem(null, 'loading', 'meters', { deletable: true }),
    registeredOwner: new AsyncItem(null, 'loading', 'text', { editable: true, deletable: true }),
    ownerType: new AsyncItem(null, 'done', 'text'),

    annualTaxes: new AsyncItem(null, 'loading', 'text', { editable: true, deletable: true }),
    taxYear: new AsyncItem(null, 'loading', 'text', { editable: true, deletable: true }),
    bathrooms: new AsyncItem(null, 'loading', 'text', { editable: true, deletable: true }),
    bedrooms: new AsyncItem(null, 'loading', 'text', { editable: true, deletable: true }),
    floorArea: new AsyncItem(null, 'loading', 'text', { editable: true, deletable: true }),
    pids: new AsyncItem(null, 'loading', 'text'),
    legalDescriptions: new AsyncItem(null, 'loading', 'text'),
    planNumber: new AsyncItem(null, 'loading', 'text'),
    planningLotSize: new AsyncItem(null, 'loading', 'text', { editable: true, deletable: true }),
    structure: new AsyncItem(null, 'loading', 'text'),
    yearBuilt: new AsyncItem(null, 'loading', 'text', { editable: true, deletable: true }),

    assessmentYear: new AsyncItem(null, 'loading', 'text'),
    assessmentYearPrev: new AsyncItem(null, 'loading', 'text'),

    currentYearBuildingValue: new AsyncItem(null, 'loading', 'currency0'),
    currentYearLandValue: new AsyncItem(null, 'loading', 'currency0'),
    currentYearTotalValue: new AsyncItem(null, 'loading', 'currency0'),

    previousYearBuildingValue: new AsyncItem(null, 'loading', 'currency0'),
    previousYearLandValue: new AsyncItem(null, 'loading', 'currency0'),
    previousYearTotalValue: new AsyncItem(null, 'loading', 'currency0'),

    buildingGrowth: new AsyncItem(null, 'loading', 'number2'),
    landGrowth: new AsyncItem(null, 'loading', 'number2'),
    totalGrowth: new AsyncItem(null, 'loading', 'number2'),

    appreciationLoaded: false,  // if true -> we can display first appreciation row
    appreciationCurrentlyListed: false, // if true -> first appreciation row is resolved by MLS, don't overwrite with Tax assessment data

    appreciationActualAmount: new AsyncItem(null, 'loading', 'currency0'),
    appreciationActualDate: new AsyncItem(null, 'loading', 'text'),
    appreciationActualGrowth: new AsyncItem(null, 'loading', 'number2'),
    salesHistory: [],

    offerRentTaskRun: false,
    showOfferRent: new EditableBoolean(true),
    offerRentDate: new AsyncItem(null, 'loading', 'date'),
    offerRentMax: new AsyncItem(null, 'loading', 'currency0'),
    offerRentMin: new AsyncItem(null, 'loading', 'currency0'),

    showMlsHistory: new EditableBoolean(false),
    showMlsHistoryFirm: new EditableBoolean(true),
    mlsHistory: new AsyncItem([], 'loading', 'text', { editable: true }),

    planningPlanningLine: new AsyncItem(null, 'loading', 'text'),
    planningZoningLine: new AsyncItem(null, 'loading', 'text'),
    planningDevelopmentApplications: new AsyncItem(null, 'loading', 'text', { editable: true }),

    attributions: [],
};


type Result<T> = (T & { status: true }) | { status: false, reason: string };


const resolveField = <T extends Record<keyof SummaryData, any>>(summary: SummaryData, fieldKey: keyof SummaryData, result: Result<T>) => {
    if (summary[fieldKey] instanceof AsyncItem || summary[fieldKey] instanceof EditableBoolean) {
        return result.status ? summary[fieldKey].resolve(result[fieldKey]) : summary[fieldKey].reject(result.reason);
    }
    return result.status ? result[fieldKey] : null;
};


const loadingField = (summary: SummaryData, fieldKey: keyof SummaryData) => {
    if (summary[fieldKey] instanceof AsyncItem) {
        return summary[fieldKey].start();
    }
    return null;
};



const updateAppreciation = (summary: SummaryData) => {
    const appreciation: Partial<SummaryData> = {};

    if (
        !summary.appreciationLoaded
        && (!summary.showMlsHistory.value || summary.loaded['SummarySheetMlsHistory'])
        && summary.loaded['SummarySheetParagonTax'])
    {
        const lastMlsItem = summary.mlsHistory.value?.[0];
        if (lastMlsItem) {
            appreciation.appreciationCurrentlyListed = lastMlsItem.status === 'Active' || lastMlsItem.status === 'Current';

            appreciation.appreciationActualAmount = new AsyncItem(lastMlsItem.lp, 'done', 'currency0');
            appreciation.appreciationActualDate = new AsyncItem(lastMlsItem.date, 'done', 'date');
        }

        if (!appreciation.appreciationCurrentlyListed) {
            appreciation.appreciationActualAmount = summary.currentYearTotalValue;
            appreciation.appreciationActualDate = summary.currentYearTotalValue.value
                ? summary.assessmentYear
                : new AsyncItem(null, 'done', 'text');
        }

        appreciation.appreciationLoaded = true;
    }

    summary = { ...summary, ...appreciation };

    // calculate growth
    const previousValue = summary.salesHistory?.[0]?.amount.value || summary.previousYearTotalValue.value;

    summary.appreciationActualGrowth = summary.appreciationActualGrowth.resolve(
        calculateGrowth(summary.appreciationActualAmount.value, previousValue)
    );

    return summary;
};


export const resolveResult = <T extends Record<string, any>>(summary: SummaryData, taskKey: string, result: Result<T>) => {
    const computed: Record<string, any> = {};
    let attributions = summary.attributions;

    if (taskKey === 'SummarySheetLtsa' && !result.status && result.reason?.includes('Expired password')) {
        computed.showCredentialsMsg = true;
    }

    if (taskKey === 'SummarySheetParagonTax' && result.status) {
        computed.salesHistory = result.salesHistory ? buildSalesHistory(result.salesHistory) : [];
        computed.assessmentYearPrev = result.assessmentYear - 1;
        computed.buildingGrowth = calculateGrowth(result.currentYearBuildingValue, result.previousYearBuildingValue);
        computed.landGrowth = calculateGrowth(result.currentYearLandValue, result.previousYearLandValue);
        computed.totalGrowth = calculateGrowth(result.currentYearTotalValue, result.previousYearTotalValue);
    }

    if (taskKey === 'SummarySheetMlsHistory' && result.status) {
        attributions = [...summary.attributions, result.attribution].filter(attrib => attrib);
    }

    result = { ...result, ...computed };

    return updateAppreciation({
        ...summary,
        loaded: {
            ...summary.loaded,
            [taskKey]: true,
        },
        ...Object.fromEntries(Object.keys(summaryFields[taskKey] || {})
            .map(fieldKey => [fieldKey, resolveField(summary, fieldKey as keyof SummaryData, result)])
        ),
        attributions,
    });
};


export const loadingTask = (summary: SummaryData, taskKey: string) => {
    return {
        ...summary,
        loaded: {
            ...summary.loaded,
            [taskKey]: false,
        },
        ...Object.fromEntries(Object.keys(summaryFields[taskKey] || {})
            .map(fieldKey => [fieldKey, loadingField(summary, fieldKey as keyof SummaryData)])
        ),
    };
};


interface SummaryPdfData extends SummaryData {
    googleImage: string | null;
    attribution: string | null;
}

export interface PdfData {
    property: Parcel;
    data: SummaryPdfData;
}
